import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";

import usePageLoading from "hooks/usePageLoading";

import { TooltipClientType } from "js/components/Auth/TooltipClientType";
import {
  FormSkeleton,
  PageLoadingContainer,
  SkeletonHeading,
  SkeletonInput,
  SkeletonText,
  SlowStateBanner,
} from "js/components/Loading";
import { Button as RetryButton } from "js/components/ui/button";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";

import { clientTypeOptions } from "../../../fixtures/clientTypes";
import { useUpdateAdvisor } from "../../../hooks/useAccounts";
import { useAppSelector } from "../../../redux/hooks";
import { loadOrganizationData } from "../../../store/features/organization/organizationActions";
import { userDataUpdated } from "../../../store/features/user/user";
import { loadAdvisorData } from "../../../store/features/user/userActions";
import {
  validateBirthDate,
  validateClientType,
  validatePhoneNumber,
} from "../../../utils/validators/generalValidators";
import { validateName } from "../../../utils/validators/tenantFormValidators";

const { Title } = Typography;

const ProfileDetails = () => {
  const [form] = Form.useForm();
  const [selectedValue, setSelectedValue] = useState();
  const [userId, setUserId] = useState<string>();
  const [optionalEmail, setOptionalEmail] = useState<string>("");

  const optionalEmailInputRef = useRef<EmailInputRef>(null);

  const { userData, loading: userLoading } = useAppSelector(
    (state) => state.entities.user,
  );
  const { organizationData, loading: organizationLoading } = useAppSelector(
    (state) => state.entities.organization,
  );
  const dispatch = useDispatch();

  const updateMutation = useUpdateAdvisor();

  const onFinish = async (formValues: any) => {
    // Validate optional email if provided
    if (optionalEmail) {
      const isValid = await optionalEmailInputRef.current?.validate();
      if (!isValid) {
        return;
      }
    }

    // Include the optionalEmail from state
    formValues.optional_email = optionalEmail;

    if (formValues.birth_date == null) {
      delete formValues.birth_date;
    } else {
      formValues.birth_date = dayjs(formValues.birth_date).format("DD/MM/YYYY");
    }

    updateMutation.mutate(
      { advisorId: userId!, data: formValues },
      {
        onSuccess: () => {
          const updatedUserData = {
            ...formValues,
            id: userId,
            email: (userData as any)?.email,
            role: (userData as any)?.role,
            organization: (userData as any)?.organization,
            is_validated: (userData as any)?.is_validated,
          };
          dispatch(userDataUpdated(updatedUserData));
        },
      },
    );
  };

  useEffect(() => {
    if (!userData) {
      return;
    }

    const formData = {
      name: (userData as any)?.name ?? "",
      first_surname: (userData as any)?.first_surname ?? "",
      second_surname: (userData as any)?.second_surname ?? "",
      phone_number: (userData as any)?.phone_number ?? "",
      optional_email: (userData as any)?.optional_email ?? "",
      birth_date: (userData as any)?.birth_date
        ? dayjs((userData as any)?.birth_date)
        : null,
      gender: (userData as any)?.gender ?? "",
      client_type: (userData as any)?.client_type ?? "",
    };
    form.setFieldsValue(formData);
    setOptionalEmail((userData as any)?.optional_email ?? "");
    setUserId((userData as any)?.id ?? "");
  }, [userData, form]);

  const style: React.CSSProperties = {
    // background: "#0092ff",
    padding: "8px 0",
  };

  const customInputStyle = {
    width: "100%",
    height: "40px",
    // fontSize: "16px",
  };

  const customDatePickerStyle = {
    width: "100%",
    height: "40px",
    // fontSize: "16px",
  };

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading:
      userLoading || organizationLoading || !userData || !organizationData,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    dispatch(loadAdvisorData());
    dispatch(loadOrganizationData());
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <RetryButton
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </RetryButton>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-6 w-32" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-6">
      <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-white/95 p-6">
        <SkeletonText className="h-4 w-72" />
        <SkeletonText className="h-4 w-80" />
        <div className="my-4 h-px bg-slate-200" />
        <SkeletonText className="h-5 w-48" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <SkeletonInput className="h-10" />
          <SkeletonInput className="h-10" />
          <SkeletonInput className="h-10" />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SkeletonInput className="h-10" />
          <SkeletonInput className="h-10" />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SkeletonInput className="h-10" />
          <SkeletonInput className="h-10" />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SkeletonInput className="h-10" />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <SkeletonInput className="h-10 w-full" />
        </div>
      </div>
    </div>
  );

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-6"
    >
      <div id="profile">
        <Title level={3}>Cuenta</Title>

        <Row justify="center">
          <Col xs={24} sm={24} md={18} lg={18} xl={18} span={18}>
            <Card>
              <Row justify="center" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={8}
                  xl={8}
                  className="gutter-row"
                  span={8}
                >
                  <p>
                    Email: <strong className="sentry-mask">{(userData as any)?.email}</strong>
                  </p>
                  <p>
                    Organización:{" "}
                    <strong>{(organizationData as any)?.name}</strong>
                  </p>
                </Col>
              </Row>

              <Divider />

              <Form
                form={form}
                name="updateAdvisorForm"
                onFinish={onFinish}
                disabled={updateMutation.isPending}
                layout="vertical"
              >
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={8}
                    xl={8}
                    className="gutter-row"
                    span={8}
                  >
                    <Title level={5}>Datos de contacto</Title>
                  </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={8}
                    xl={8}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item
                        name="name"
                        label="Nombre"
                        rules={[
                          { required: true, message: "Requerido" },
                          { validator: validateName },
                        ]}
                      >
                        <Input
                          style={customInputStyle}
                          data-sentry-ignore
                          onChange={(e) => {
                            const upperCaseValue = e.target.value.toUpperCase();
                            form.setFieldsValue({ name: upperCaseValue });
                          }}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={8}
                    xl={8}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item
                        name="first_surname"
                        label="Apellido Paterno"
                        rules={[
                          {
                            required: true,
                            message: "Requerido",
                          },
                          { validator: validateName },
                        ]}
                      >
                        <Input
                          style={customInputStyle}
                          data-sentry-ignore
                          onChange={(e) => {
                            const upperCaseValue = e.target.value.toUpperCase();
                            form.setFieldsValue({
                              first_surname: upperCaseValue,
                            });
                          }}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={8}
                    xl={8}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item
                        name="second_surname"
                        label="Apellido Materno"
                        rules={[{ validator: validateName }]}
                      >
                        <Input
                          style={customInputStyle}
                          data-sentry-ignore
                          onChange={(e) => {
                            const upperCaseValue = e.target.value.toUpperCase();
                            form.setFieldsValue({
                              second_surname: upperCaseValue,
                            });
                          }}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={12}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item
                        name="gender"
                        label="Género"
                        rules={[{ required: true, message: "Requerido" }]}
                      >
                        <Select
                          value={selectedValue}
                          // onSelect={handleChange}
                          options={[
                            { value: "M", label: "Hombre" },
                            { value: "F", label: "Mujer" },
                            { value: "N", label: "No especificar" },
                          ]}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={12}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item
                        name="birth_date"
                        label="Fecha de Nacimiento"
                        rules={[{ validator: validateBirthDate }]}
                      >
                        <DatePicker
                          changeOnBlur
                          placeholder="DD/MM/YYYY"
                          format={"DD/MM/YYYY"}
                          style={customDatePickerStyle}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={12}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item
                        name="phone_number"
                        label="Celular"
                        rules={[
                          { required: true, message: "Requerido" },
                          { validator: validatePhoneNumber },
                        ]}
                      >
                        <Input style={customInputStyle} maxLength={10} data-sentry-ignore />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={12}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <EmailInput
                        ref={optionalEmailInputRef}
                        value={optionalEmail}
                        onChange={(value) => {
                          setOptionalEmail(value);
                          form.setFieldValue("optional_email", value);
                        }}
                        label="Email secundario"
                        placeholder="email.secundario@correo.com"
                        showIcon={false}
                        showStatus={false}
                        className="h-10"
                      />
                    </div>
                  </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={12}
                    xl={12}
                    className="gutter-row"
                  >
                    <Form.Item
                      name="client_type"
                      label={
                        <Space>
                          Perfil de usuario
                          <TooltipClientType />
                        </Space>
                      }
                      rules={[
                        { required: true, validator: validateClientType },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Selecciona una opcion"
                        optionFilterProp="children"
                      >
                        {clientTypeOptions.map((option) => (
                          <Select.Option
                            value={option.value}
                            key={option.value}
                          >
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col
                    xs={24}
                    sm={24}
                    md={24}
                    lg={8}
                    xl={8}
                    className="gutter-row"
                    span={8}
                  >
                    <div style={style}>
                      <Form.Item>
                        <Button
                          type="primary"
                          style={{ width: "100%" }}
                          htmlType="submit"
                          loading={updateMutation.isPending}
                        >
                          {updateMutation.isPending
                            ? "Actualizando..."
                            : "Actualizar"}
                        </Button>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </PageLoadingContainer>
  );
};

export default ProfileDetails;
